import type {
  ArtworkOption,
  ArtworkOverlayGroup,
  ArtworkOverlayOption,
  OverlaySelectionsByGroup
} from './digitalProofingArtworkTypes'
import { Fragment } from 'react'
import { FormGroup, Input, Label } from 'reactstrap'
import ArtworkPreviewSurface from './ArtworkPreviewSurface'
import {
  NO_SELECTION_ID,
  groupOptions,
  isRequiredSingleOptionGroup,
  normalizedSelectionId,
  overlayGroupId,
  sortByPosition
} from './digitalProofingArtworkFormUtils'

type ArtworkOverlayGroupControlsProps = {
  isDigitalProofingProduct?: boolean
  selectedArtwork?: ArtworkOption | false | null
  visibleOverlayGroups: ArtworkOverlayGroup[]
  selectedOverlayIdsByGroup: OverlaySelectionsByGroup
  overlayOptionsById: Record<string, ArtworkOverlayOption>
  onSelectOverlayGroup: (group: ArtworkOverlayGroup, selectedId: string) => void
  onToggleOverlayGroupOption: (group: ArtworkOverlayGroup, optionId: string, checked: boolean) => void
}

const artworkSelectOptions = (options: ArtworkOverlayOption[]) =>
  sortByPosition(options).map((option, index) => {
    const id = normalizedSelectionId(option)

    return (
      <option key={id ?? index} value={id}>
        {option.name || option.label}
      </option>
    )
  })

const renderOverlayOptionThumbnail = (option?: ArtworkOverlayOption) =>
  option?.image2 ? (
    <>
      <br />
      <ArtworkPreviewSurface
        src={option.image2}
        alt={`${option.label || option.name} thumbnail`}
        className="artwork-preview-surface--overlay-option"
      />
    </>
  ) : null

const ArtworkOverlayGroupControls = ({
  isDigitalProofingProduct,
  selectedArtwork,
  visibleOverlayGroups,
  selectedOverlayIdsByGroup,
  overlayOptionsById,
  onSelectOverlayGroup,
  onToggleOverlayGroupOption
}: ArtworkOverlayGroupControlsProps) => {
  if (!isDigitalProofingProduct || !selectedArtwork) return null

  const renderSelectedOverlayThumbnails = (group: ArtworkOverlayGroup) => {
    const selectedIds = selectedOverlayIdsByGroup[overlayGroupId(group)] ?? []
    return (
      <>
        {selectedIds.map(id => (
          <Fragment key={id}>{renderOverlayOptionThumbnail(overlayOptionsById[id])}</Fragment>
        ))}
      </>
    )
  }

  const renderGroupControl = (group: ArtworkOverlayGroup) => {
    const selectedIds = selectedOverlayIdsByGroup[overlayGroupId(group)] ?? []
    const selectedId = selectedIds[0] ?? NO_SELECTION_ID
    const options = groupOptions(group)
    const minimumSelections = Number(group.min_selections)
    const maximumSelections = Number(group.max_selections)

    if (options.length === 0) {
      return (
        <div className="mf-mb-20 w-100">
          <br />
          <h6>{group.label}</h6>
          {group.help_text && <p className="small-help">{group.help_text}</p>}
          <p>No available options</p>
          {minimumSelections > 0 && <p className="red-text">Please select at least {minimumSelections}.</p>}
        </div>
      )
    }

    if (isRequiredSingleOptionGroup(group) && options.length === 1) {
      return (
        <div className="mf-mb-20 w-100">
          <br />
          <h6>{group.label}</h6>
          {group.help_text && <p className="small-help">{group.help_text}</p>}
          {options[0].label || options[0].name}
          {renderOverlayOptionThumbnail(options[0])}
        </div>
      )
    }

    if (maximumSelections === 1) {
      return (
        <div className="mf-mb-20 w-100">
          <br />
          <h6>{group.label}</h6>
          {group.help_text && <p className="small-help">{group.help_text}</p>}
          <Input value={selectedId} onChange={event => onSelectOverlayGroup(group, event.target.value)} type="select">
            <option disabled={minimumSelections > 0} value={NO_SELECTION_ID}>
              {minimumSelections > 0 ? `Please select ${group.label}` : 'No selection'}
            </option>
            {artworkSelectOptions(options)}
          </Input>
          {renderSelectedOverlayThumbnails(group)}
        </div>
      )
    }

    return (
      <div className="mf-mb-20 w-100">
        <br />
        <h6>{group.label}</h6>
        {group.help_text && <p className="small-help">{group.help_text}</p>}
        {options.map(option => {
          const optionId = normalizedSelectionId(option)
          const checked = selectedIds.includes(optionId)
          const disabled = !checked && selectedIds.length >= maximumSelections

          return (
            <FormGroup check key={optionId}>
              <Label check>
                <Input
                  checked={checked}
                  disabled={disabled}
                  onChange={event => onToggleOverlayGroupOption(group, optionId, event.target.checked)}
                  type="checkbox"
                />{' '}
                {option.label || option.name}
              </Label>
            </FormGroup>
          )
        })}
        {minimumSelections > 0 && selectedIds.length < minimumSelections && (
          <p className="red-text">Please select at least {minimumSelections}.</p>
        )}
        {maximumSelections > 1 && renderSelectedOverlayThumbnails(group)}
      </div>
    )
  }

  return (
    <>
      {visibleOverlayGroups.map(group => (
        <Fragment key={group.id}>{renderGroupControl(group)}</Fragment>
      ))}
    </>
  )
}

export default ArtworkOverlayGroupControls
